import { FC, useEffect, useRef, useState } from 'react';
import { FareFamilyInfo, FlightSearch } from 'ts/flight/interfaces/FlightSearch';
import { getBaggagePrice } from 'services/flight';
import { formatPriceCurrency, getCurrency } from 'utils/utils';
import Skeleton from 'react-loading-skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faDollarSign, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import LoadingBlock from 'components/shared/LoadingBlock';
import RulesModal from '../Book/RulesModal';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { Link } from 'react-router-dom';
import { b2b } from 'constants/generic';

interface FlightPairsFLexProps {
  flight: FlightSearch;
  sessionStorageKey: string;
}

const FlightPairsFLex: FC<FlightPairsFLexProps> = ({ ...props }) => {
  const [flights, setFlights] = useState<FlightSearch[]>();
  const [flexFlightId, setFlexFlightId] = useState<string>();
  const searchToken = useRef(sessionStorage.getItem(props.sessionStorageKey) || '');
  useEffect(() => {
    (async () => {
      const response = await getBaggagePrice({
        searchToken: searchToken.current,
        id: props.flight.id,
        getFamilyPlan: true,
      });

      setFlights(response?.bookFareAndBaggage?.flights || []);
    })();
  }, [props.flight]);

  const getAllOfFlexDesc = () => {
    const allDesc = flights
      ?.map((fl) => fl.fareFamilyInfo?.[0]?.fareFamilyDescription.map((ff) => ff.desc).flat())
      .flat()
      .filter((desc, index, self) => self.indexOf(desc) === index);

    return allDesc || [];
  };

  const getFareCode = (fare: FareFamilyInfo, fareSm: string) => {
    const fareCode = fare.fareFamilyDescription.find((ff) => ff.desc === fareSm)?.code;

    if (fareCode === 'INC') {
      return (
        <span>
          <FontAwesomeIcon icon={faCheckCircle as any} className="text-success mr-3" />
          INC
        </span>
      );
    }
    if (fareCode === 'CHA') {
      return (
        <span>
          <FontAwesomeIcon icon={faDollarSign as any} className="mr-3" />
          CHA
        </span>
      );
    }

    return (
      <span>
        <FontAwesomeIcon icon={faTimesCircle as any} className="color-danger mr-3" />
        NOF
      </span>
    );
  };

  return (
    <div className="flex-one flex-column text-sm">
      {!flights && (
        <div className="fl-pair-flex-wr">
          {b2b && <LoadingBlock />}
          <div className="mt-20 text-xsm">
            {[...new Array(2)]?.map((_, fareIndex) => (
              <div
                key={fareIndex}
                className="table-default mb-30 table-border-contrast table-small"
              >
                <table className="table-bordered-lines table-mobile">
                  <thead>
                    <tr>
                      <th
                        colSpan={5}
                        style={{ background: 'var(--color-contrast)', color: 'var(--color-0)' }}
                      >
                        <Skeleton width={100} />
                      </th>
                    </tr>
                    <tr>
                      <th>
                        <Skeleton width={20} />
                      </th>
                      <th>
                        <Skeleton width={40} />
                      </th>
                      {[...new Array(3)]?.map((_, flexIndex) => (
                        <th className="text-cap" key={flexIndex}>
                          <Skeleton width={50} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...new Array(22)]?.map((_, fareSmIndex) => (
                      <tr key={fareSmIndex}>
                        <td>
                          <Skeleton width={20} />
                        </td>
                        <td className="text-cap">
                          <Skeleton width={70} />
                        </td>
                        {[...new Array(3)]?.map((_, flexIndex) => (
                          <td key={flexIndex}>
                            <Skeleton width={40} />
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr></tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
      {!!flights && (
        <div className="fl-pair-flex-wr">
          <div className="fl-pair-flex-head">
            {flights?.map((fl, index) => (
              <div key={index} className="fl-pair-flex-item">
                <div className="cur-poi" onClick={() => setFlexFlightId(fl.id)}>
                  {fl.fareFamilyInfo?.[0]?.desc?.toLowerCase()}
                </div>
                <div className="cur-poi" onClick={() => setFlexFlightId(fl.id)}>
                  {fl.fareFamilyInfo?.[0]?.desc?.toLowerCase()}
                </div>
                <div className="text-bold text-md">
                  <Tooltip
                    content={'Go to Book'}
                    children={
                      <Link
                        to={`/flight/book?id=${fl.id}&searchToken=${searchToken.current}`}
                        className="cur-poi"
                        target="_blank"
                      >
                        {formatPriceCurrency(
                          fl.flighPrice.grossAndTaxWithMarkup.value,
                          getCurrency(fl.flighPrice.grossAndTaxWithMarkup.currency)
                        )}
                      </Link>
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-20 text-xsm">
            {flights?.[0]?.fareFamilyInfo.map((fare, fareIndex) => (
              <div
                key={fareIndex}
                className="table-default mb-30 table-border-contrast table-small"
                style={{ overflow: 'auto' }}
              >
                <table className="table-bordered-lines table-mobile ">
                  <thead>
                    <tr>
                      <th
                        colSpan={flights.length + 2}
                        style={{ background: 'var(--color-contrast)', color: 'var(--color-0)' }}
                      >
                        Fare info {fare.legName?.replaceAll('-', ' ')}
                      </th>
                    </tr>
                    <tr>
                      <th>#</th>
                      <th>Service</th>
                      {flights
                        .filter(
                          (fl, index, arr) =>
                            arr.findIndex(
                              (f) => f.fareFamilyInfo?.[0]?.desc === fl.fareFamilyInfo?.[0]?.desc
                            ) === index
                        )
                        .map((flex, flexIndex) => (
                          <th className="text-cap" key={flexIndex}>
                            {flex.fareFamilyInfo?.[0]?.desc?.toLowerCase()}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {getAllOfFlexDesc().map((fareSm, fareSmIndex) => (
                      <tr key={fareSmIndex}>
                        <td width={12}>{fareSmIndex + 1}</td>
                        <td className="text-cap">{fareSm?.toLowerCase()}</td>
                        {flights
                          .filter(
                            (fl, index, arr) =>
                              arr.findIndex(
                                (f) => f.fareFamilyInfo?.[0]?.desc === fl.fareFamilyInfo?.[0]?.desc
                              ) === index
                          )
                          .map((flex, fIndex) => (
                            <td key={fIndex}>{getFareCode(flex.fareFamilyInfo?.[0], fareSm)}</td>
                          ))}
                      </tr>
                    ))}
                    <tr></tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}

      {!!flexFlightId && (
        <RulesModal
          flightId={flexFlightId}
          searchToken={searchToken.current}
          onClose={() => setFlexFlightId(undefined)}
        />
      )}
    </div>
  );
};

export default FlightPairsFLex;
